// HU-27 · HU-28 · El caso dentro de su expediente: la cabecera (estado, gravedad, reaperturas y
// «cerrado por») y el historial de cierres y reaperturas (`ui/MG-ILE-54.md`, «Cabecera» y «Cierre
// del caso»; `ui/MG-ILE-56.md`, «Historial conservado»).
//
// TODO SALE DE `caso.*` DEL EXPEDIENTE (`CasoDelExpedienteSalida`, §8.1 R1). El estado y la
// gravedad llegan con su texto y se pintan tal cual; el glosario solo da el TONO de la insignia y
// el nombre de la gravedad anterior y de cada acto del historial («Cierre», «Reapertura»), que
// llegan como código. Lo ven todos los que abren el expediente: antes el estado vivía en la tarjeta
// «Cierre del caso», que solo ven tres roles, y salía de `GET .../cierre`.

import { History } from 'lucide-react';
import { Card, DataTable, Mono, StatusBadge } from './ui';
import type { ColumnaDeTabla, FilaDeTabla } from './ui';
import type { CasoDelExpediente, PasoDelCierre } from '../api/expedientes';
import { comoFechaDeInstante } from '../lib/fechas';
import {
  etiquetaDeLaGravedad, etiquetaDelActoDelCierre, tonoDeLaGravedad, tonoDelEstadoDelCaso,
} from '../lib/etiquetas';

/**
 * La línea de la cabecera: «Estado del caso [Cerrado] cerrado por Ana Ríos · 29/09/2026 ·
 * Reabierto 1 vez · Gravedad [Grave] antes No grave».
 */
export function CabeceraDelCaso({ caso, zonaHoraria }: {
  caso: CasoDelExpediente;
  /** RN-82: la zona del país del caso (`useZonaDelPais`); `null` = Lima. */
  zonaHoraria: string | null;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4 text-body" data-testid="expediente-cabecera-del-caso">
      <span className="inline-flex flex-wrap items-center gap-2">
        <span className="text-ink-soft">Estado del caso</span>
        <StatusBadge tone={tonoDelEstadoDelCaso(caso.estado_del_caso)} data-testid="expediente-estado">
          {caso.texto_del_estado}
        </StatusBadge>
        {caso.cerrado_por && (
          // HU-27-C7: quién cerró sale de la sesión del que cerró, no de lo que se escribe.
          <span className="text-meta text-ink-soft" data-testid="expediente-cierre-registro">
            cerrado por <span className="text-ink">{caso.cerrado_por}</span>
            {caso.cerrado_en && <> · <Mono>{comoFechaDeInstante(caso.cerrado_en, zonaHoraria)}</Mono></>}
          </span>
        )}
        {caso.texto_de_reaperturas && (
          <span className="text-meta text-ink-soft" data-testid="expediente-reaperturas">{caso.texto_de_reaperturas}</span>
        )}
      </span>
      <span className="inline-flex flex-wrap items-center gap-2">
        <span className="text-ink-soft">Gravedad</span>
        <StatusBadge tone={tonoDeLaGravedad(caso.gravedad)} data-testid="expediente-gravedad">
          {caso.texto_de_la_gravedad}
        </StatusBadge>
        {caso.gravedad_anterior && (
          // HU-28-C7: la reclasificación se ve, con la gravedad de antes.
          <span className="text-meta text-ink-soft" data-testid="expediente-gravedad-anterior">
            antes {etiquetaDeLaGravedad(caso.gravedad_anterior)}
          </span>
        )}
      </span>
    </div>
  );
}

const COLUMNAS: ColumnaDeTabla[] = [
  { key: 'acto', label: 'Acto', testid: 'expediente-cierres-fila-acto' },
  { key: 'quien', label: 'Quién', testid: 'expediente-cierres-fila-quien' },
  { key: 'cuando', label: 'Cuándo', testid: 'expediente-cierres-fila-cuando' },
  { key: 'motivo', label: 'Motivo', className: 'min-w-[14rem]', testid: 'expediente-cierres-fila-motivo' },
  { key: 'gravedad', label: 'Gravedad', testid: 'expediente-cierres-fila-gravedad' },
];

function celda(fila: FilaDeTabla, { key }: ColumnaDeTabla, zonaHoraria: string | null) {
  const p = fila as unknown as PasoDelCierre;
  if (key === 'acto') return <span className="font-semibold text-ink whitespace-nowrap">{etiquetaDelActoDelCierre(p.accion)} {p.orden}</span>;
  if (key === 'quien') return p.quien;
  if (key === 'cuando') return <Mono>{comoFechaDeInstante(p.cuando, zonaHoraria)}</Mono>;
  if (key === 'motivo') return p.motivo ?? '';
  if (key === 'gravedad' && p.gravedad_nueva) {
    return (
      <span className="whitespace-nowrap">
        {etiquetaDeLaGravedad(p.gravedad_anterior)} → {etiquetaDeLaGravedad(p.gravedad_nueva)}
      </span>
    );
  }
  return null;
}

/**
 * HU-28-C3 y C4: «Cierre 1 · 29/09/2026 · Ana Ríos», «Reapertura 1 · 06/11/2026 · M. Díaz ·
 * {motivo}», «Cierre 2 · …». Sin cierres no hay historial que contar y la tarjeta no se pinta.
 */
export function HistorialDeCierres({ cierres, zonaHoraria }: {
  cierres: PasoDelCierre[];
  /** RN-82: la zona del país del caso (`useZonaDelPais`); `null` = Lima. */
  zonaHoraria: string | null;
}) {
  if (!cierres.length) return null;
  return (
    <Card className="mt-4" title="Cierres y reaperturas" icon={History} pad={false}
      subtitle="Cada cierre y cada reapertura del caso, con quién, cuándo y por qué. Reabrir no borra los cierres anteriores.">
      <DataTable columns={COLUMNAS} rows={cierres.map((p) => ({ ...p, id: `${p.accion}-${p.orden}` }))} renderCell={(fila, columna) => celda(fila, columna, zonaHoraria)} sinMarco
        data-testid="expediente-cierres" testidDeFila="expediente-cierres-fila" regionDesplazable="Cierres y reaperturas" />
    </Card>
  );
}
